import { ReactElement } from 'react';
import { Field, useFormikContext, FieldArray, FormikValues, FieldArrayRenderProps, getIn, useField } from 'formik';
import { FormType } from '@constants';
import { TextInput, DateInput } from '@components';
import TrashIcon from '../../images/trash.svg';
import styles from './MultiTextInput.module.scss';

interface iProps {
  name: string;
  formName: string;
  stepNo: string;
  includeRegisteredFines?: boolean;
  includeUnregisteredFines?: boolean;
}

const MultiTextInput = ({ name, formName, stepNo, includeRegisteredFines = false, includeUnregisteredFines = false }: iProps): ReactElement => {
  const { values }: { values: FormikValues } = useFormikContext();
  const formikContext = useFormikContext();

  // For the thirty party form
  const obligationList = useField('obligations')[1];
  const isAddFineBtnDisabled = (): boolean => {
    if (formName !== FormType.THIRDPARTY) {
      return false;
    }
    if (formName === FormType.THIRDPARTY && obligationList && obligationList?.value?.length === 25) {
      return true;
    }
    if (!obligationList.touched) {
      return true;
    }
    if (obligationList.touched && obligationList.error === undefined) {
      // no error
      return false;
    }
    return true;
  };

  const addFine = (arrayHelpers: FieldArrayRenderProps | { value: string }[]): ReactElement => {
    if (formName && formName === FormType.FAMILYVIOLENCESCHEME && (values.enteredObligationsArray?.length >= 10 || values.infringementFineArray?.length >= 10)) {
      return <></>;
    }
    if (values.RegisteredFinesToInclude === 'true' && includeRegisteredFines) {
      return (
        <button type="button" className={styles.addNewSection} onClick={() => arrayHelpers.push({ value: '' })}>
          + Add another registered fine
        </button>
      );
    }
    if (values.InfringementFinesToInclude === 'true' && includeUnregisteredFines) {
      return (
        <button type="button" className={styles.addNewSection} onClick={() => arrayHelpers.push({ value: '' })}>
          + Add another unregistered fine
        </button>
      );
    }

    return (
      <button
        type="button"
        className={styles.addNewSection}
        disabled={isAddFineBtnDisabled()}
        onClick={() => {
          if (formName === FormType.FAMILYVIOLENCESCHEME && (stepNo === 'step9' || stepNo === 'step18') && includeUnregisteredFines) {
            arrayHelpers.push({ value: '' });
            formikContext.setFieldValue('AgencyNames', [...getIn(values, 'AgencyNames'), { value: '' }]);
            formikContext.setFieldValue('InfringementNums', [...getIn(values, 'InfringementNums'), { value: '' }]);
            formikContext.setFieldValue('AOffence', [...getIn(values, 'AOffence'), { value: '' }]);
            formikContext.setFieldValue('DAOffence', [...getIn(values, 'DAOffence'), { value: '' }]);
          } else arrayHelpers.push({ value: '' });
        }}>
        + Add another fine
      </button>
    );
  };
  const removeFines = (arrayHelpers: any, removedIndex: number) => {
    arrayHelpers.remove(removedIndex);
    if (formName === FormType.FAMILYVIOLENCESCHEME && (stepNo === 'step9' || stepNo === 'step18') && includeUnregisteredFines) {
      getIn(values, 'AgencyNames')?.splice(removedIndex, 1).pop();
      getIn(values, 'InfringementNums')?.splice(removedIndex, 1).pop();
      getIn(values, 'AOffence')?.splice(removedIndex, 1).pop();
      getIn(values, 'DAOffence')?.splice(removedIndex, 1).pop();
      formikContext.setFieldValue('AgencyNames', [...getIn(values, 'AgencyNames')]);
      formikContext.setFieldValue('InfringementNums', [...getIn(values, 'InfringementNums')]);
      formikContext.setFieldValue('AOffence', [...getIn(values, 'AOffence')]);
      formikContext.setFieldValue('DAOffence', [...getIn(values, 'DAOffence')]);
    }
  };

  return (
    <FieldArray
      name={name}
      render={(arrayHelpers) => (
        <div>
          {values[name].map((_item: any, index: number) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={index} className={styles.section}>
              {formName !== FormType.THIRDPARTY && (
                <div className={styles.descriptionDiv}>
                  {formName === FormType.FAMILYVIOLENCESCHEME && (!includeUnregisteredFines || stepNo === 'step9') ? (
                    <p>
                      {stepNo === 'step9' ? (
                        <>{`Fine ${index + 1}`}</>
                      ) : (
                        <>
                          Registered Fine
                          {` ${index + 1}`}
                        </>
                      )}
                      {stepNo === 'step9' ? (
                        <>
                          <br />
                          <span className={styles.subTitle}>(please complete as many field as possible)</span>
                        </>
                      ) : (
                        ''
                      )}
                    </p>
                  ) : (
                    <></>
                  )}
                  {formName === FormType.FAMILYVIOLENCESCHEME && stepNo === 'step18' && includeUnregisteredFines && (
                    <p>
                      Infringement stage fine
                      {` ${index + 1}`}
                    </p>
                  )}
                  {values[name].length > 1 && (
                    <button type="button" onClick={() => removeFines(arrayHelpers, index)} className={styles.removeDiv}>
                      <span className={styles.remove}>Remove</span>
                      <img src={TrashIcon} alt="trash icon" />
                    </button>
                  )}
                </div>
              )}
              {formName === FormType.THIRDPARTY && stepNo === 'step2' && (
                <div className={`${styles.descriptionDiv} ${formName === FormType.THIRDPARTY ? styles.inline : ''}`}>
                  <div className={styles.colHalf}>
                    <Field name={`obligations.${index}.value`} component={TextInput} title={`Enter obligation ${index + 1}`} isMandatory />
                  </div>

                  {values[name].length > 1 && (
                    <button type="button" onClick={() => removeFines(arrayHelpers, index)} className={styles.removeDiv}>
                      <span className={styles.remove}>Remove</span>
                      <img src={TrashIcon} alt="trash icon" />
                    </button>
                  )}
                </div>
              )}
              {formName === FormType.FAMILYVIOLENCESCHEME && (stepNo === 'step9' || stepNo === 'step18') && includeUnregisteredFines && (
                <>
                  <Field name={`AgencyNames.${index}.value`} component={TextInput} title="Enforcement agency (the organisation that issued the fine)" isMandatory />
                  <Field name={`InfringementNums.${index}.value`} component={TextInput} title="Infringement notice number" />
                  <Field name={`AOffence.${index}.value`} component={TextInput} title="Alleged offence" />
                  <Field name={`DAOffence.${index}.value`} component={DateInput} title="Date of alleged offence" maxDate={new Date()} />
                </>
              )}
              {formName === FormType.FAMILYVIOLENCESCHEME && stepNo === 'step18' && includeRegisteredFines && (
                <Field name={`obligations.${index}.value`} component={TextInput} title="Obligation number or enforcement agency identifying reference" isMandatory />
              )}
            </div>
          ))}
          <br />
          {formName === FormType.THIRDPARTY && typeof obligationList.error === 'string' && <p className={styles.error}>{obligationList.error}</p>}
          {addFine(arrayHelpers)}
        </div>
      )}
    />
  );
};

export default MultiTextInput;
